// src/views/RegisterPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaFacebookF, FaGoogle } from 'react-icons/fa';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from '../firebaseConfig';
import logoXCoin from '../assets/logo_plateado.png';
import background from '../assets/backhome.png';

// --- 1. GENERACIÓN DEL ARRAY DE AVATARES ---
// Usamos la misma lógica que en AvatarPickerModal.jsx para tener los mismos avatares
const AVATARS_DISPONIBLES = Array.from({ length: 9 }, (_, i) => `https://i.pravatar.cc/150?u=${i + 1}`);

// --- 2. FUNCIÓN PARA ELEGIR UNO AL AZAR ---
const obtenerAvatarAleatorio = () => {
  const indiceAleatorio = Math.floor(Math.random() * AVATARS_DISPONIBLES.length);
  return AVATARS_DISPONIBLES[indiceAleatorio];
};

const RequiredAsterisk = () => (
  <span title="Campo obligatorio" style={{ color: '#dc3545', cursor: 'help', fontWeight: 'bold' }}> *</span>
);

// --- VALIDACIÓN RUT CHILENO (Módulo 11) ---
const limpiarRut = (rut) => rut.replace(/[^0-9kK]/g, '');

const formatearRut = (rut) => {
  const limpio = limpiarRut(rut);
  if (limpio.length <= 1) return limpio;
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1).toUpperCase();
  let cuerpoFormateado = '';
  let count = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    count++;
    if (count % 3 === 0 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
    }
  }
  return `${cuerpoFormateado}-${dv}`;
};

const validarRut = (rut) => {
  const limpio = limpiarRut(rut);
  if (limpio.length < 8 || limpio.length > 9) return false;
  const cuerpo = limpio.slice(0, -1);
  const dvIngresado = limpio.slice(-1).toUpperCase();
  if (!/^\d+$/.test(cuerpo)) return false;
  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = suma % 11;
  const dvCalculado = 11 - resto;
  let dvEsperado;
  if (dvCalculado === 11) dvEsperado = '0';
  else if (dvCalculado === 10) dvEsperado = 'K';
  else dvEsperado = dvCalculado.toString();
  return dvIngresado === dvEsperado;
};

// --- VALIDACIÓN EMAIL ---
const validarEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

const RegisterPage = () => {
  const [nombre, setNombre] = useState('');
  const [username, setUsername] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [rut, setRut] = useState('');
  const [numeroPasaporte, setNumeroPasaporte] = useState('');
  const [telefono, setTelefono] = useState('');
  const [pais, setPais] = useState('');
  const [direccion, setDireccion] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [rutError, setRutError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [cargando, setCargando] = useState(false);

  const navigate = useNavigate();
  const marketingUrl = import.meta.env.VITE_MARKETING_APP_URL || 'http://localhost:5173';

  const [searchParams] = useSearchParams();
  const isModal = searchParams.get('modal') === 'true';

  const formSideStyle = isModal ? {
    width: '100%',
    padding: '0',
    maxHeight: 'none',
    height: 'auto',
    alignItems: 'center'
  } : {};

  const backgroundSideStyle = isModal ? {
    display: 'none'
  } : {
    backgroundImage: `url(${background})`
  };

  const handleSocialLogin = async (providerType) => {
    setCargando(true);
    setError(null);
    try {
      let provider;
      if (providerType === 'google') {
        provider = new GoogleAuthProvider();
      } else if (providerType === 'facebook') {
        provider = new FacebookAuthProvider();
      }

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 3. Guardar datos en Firestore mediante la Cloud Function
      // Nota: No tenemos 'username' ni 'rut' ni 'tipoDocumento' desde redes sociales.
      // Se enviarán nulos y el usuario deberá completarlos en su perfil si es necesario.
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');

      await gestionarPerfil({
        nombre: user.displayName,
        email: user.email,
        fotoURL: user.photoURL,
        // Enviamos null para que la función use lógica por defecto o los deje vacíos
        username: null,
        tipoDocumento: null,
        rut: null,
        numeroPasaporte: null,
        telefono: null,
        pais: null,
        direccion: null
      });

      if (isModal) {
        navigate('/login?modal=true');
      } else {
        navigate('/');
      }

    } catch (err) {
      console.error("Error login social:", err);
      if (err.code === 'auth/account-exists-with-different-credential') {
        setError('Ya existe una cuenta con este email usando otro método de ingreso.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Inicio de sesión cancelado.');
      } else {
        setError('Ocurrió un error al iniciar sesión con redes sociales.');
      }
    } finally {
      setCargando(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setCargando(true);
    setError(null);

    if (!validarEmail(email)) {
      setError("El correo electrónico no tiene un formato válido.");
      setEmailError("Formato inválido (ej: usuario@dominio.com)");
      setCargando(false);
      return;
    }
    setEmailError('');

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      setCargando(false);
      return;
    }

    if (tipoDocumento === 'cedula' && !rut) {
      setError("Debes ingresar tu RUT.");
      setCargando(false);
      return;
    }
    if (tipoDocumento === 'cedula' && !validarRut(rut)) {
      setError("El RUT ingresado no es válido.");
      setRutError("RUT inválido. Verifica el dígito verificador.");
      setCargando(false);
      return;
    }
    setRutError('');

    if (tipoDocumento === 'pasaporte' && !numeroPasaporte) {
      setError("Debes ingresar tu número de pasaporte.");
      setCargando(false);
      return;
    }

    try {
      // 1. Crear el usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // --- 3. SELECCIONAR AVATAR ALEATORIO (Desde pravatar.cc) ---
      const avatarAleatorio = obtenerAvatarAleatorio(); // Elegirá una URL tipo https://i.pravatar.cc/150?u=X

      // 2. Actualizar perfil de Auth con el nombre y la foto
      await updateProfile(user, {
        displayName: nombre,
        photoURL: avatarAleatorio
      });

      // 3. Guardar datos en Firestore mediante la Cloud Function
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');

      await gestionarPerfil({
        username: username,
        nombre: nombre,
        tipoDocumento: tipoDocumento,
        rut: rut,
        numeroPasaporte: numeroPasaporte,
        telefono: telefono,
        pais: pais,
        direccion: direccion,
        // Enviamos también la fotoURL a la base de datos
        fotoURL: avatarAleatorio
      });

      // 4. Enviar correo de verificación PERSONALIZADO (en español, profesional)
      const sendVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
      try {
        await sendVerificationEmail();
        console.log('Correo de verificación personalizado enviado');
      } catch (emailError) {
        console.warn('Error enviando correo personalizado, usando método por defecto:', emailError);
        // Fallback al método genérico de Firebase si falla
        await sendEmailVerification(user);
      }

      if (isModal) {
        navigate('/login?modal=true');
      } else {
        // Redirigir a la vista de verificación
        navigate('/verify-email');
      }

    } catch (err) {
      if (err.code === 'functions/already-exists') {
        setError('Este nombre de usuario ya está en uso. Por favor, elige otro.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('Este correo electrónico ya está en uso.');
      } else if (err.code === 'auth/weak-password') {
        setError('La contraseña debe tener al menos 6 caracteres.');
      } else {
        setError('Ocurrió un error durante el registro.');
      }
      console.error("Error de registro:", err);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className={`login-page-wrapper ${isModal ? 'modal-mode' : ''}`}>
      <div className="form-side" style={formSideStyle}>
        <div className="form-content">

          {!isModal && (
            <div style={{ marginBottom: '1rem' }}>
              <a href={marketingUrl} className="btn btn-back-home">
                &larr; Volver al Inicio
              </a>
            </div>
          )}

          <div className="form-header">
            <img src={logoXCoin} alt="XCoin Logo" className="logo" style={{ width: '300px', height: 'auto' }} />
          </div>
          <div className="form-body">
            <h2>Crea tu cuenta</h2>
            <p>Llena los siguientes campos para registrarte.</p>

            <form onSubmit={handleRegister}>
              <div className="input-group">
                <label>Nombre Completo<RequiredAsterisk /></label>
                <input type="text" placeholder="Ej: Juan Pérez" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
              </div>
              <div className="input-group">
                <label>Nombre de Usuario<RequiredAsterisk /></label>
                <input type="text" placeholder="Ej: juanperez99" value={username} onChange={(e) => setUsername(e.target.value)} required />
              </div>
              <div className="input-group">
                <label>Correo Electrónico<RequiredAsterisk /></label>
                <input
                  type="email"
                  placeholder="ejemplo@gmail.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (e.target.value && !validarEmail(e.target.value)) {
                      setEmailError('Formato inválido (ej: usuario@dominio.com)');
                    } else {
                      setEmailError('');
                    }
                  }}
                  required
                  style={emailError ? { borderColor: '#dc3545' } : {}}
                />
                {emailError && <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>{emailError}</small>}
              </div>

              <div className="input-group">
                <label>Teléfono</label>
                <input type="tel" placeholder="+56 9 1234 5678" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
              </div>
              <div className="input-group">
                <label>País</label>
                <input type="text" placeholder="Ej: Chile" value={pais} onChange={(e) => setPais(e.target.value)} />
              </div>

              <div className="input-group">
                <label>Tipo de Documento<RequiredAsterisk /></label>
                <select
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  required
                >
                  <option value="">Seleccionar...</option>
                  <option value="cedula">Cédula de Identidad</option>
                  <option value="pasaporte">Pasaporte</option>
                  <option value="anonimo">Anónimo</option>
                </select>
              </div>

              {tipoDocumento === 'cedula' && (
                <div className="input-group">
                  <label>RUT<RequiredAsterisk /></label>
                  <input
                    type="text"
                    placeholder="Ej: 12.345.678-9"
                    value={rut}
                    onChange={(e) => {
                      const formateado = formatearRut(e.target.value);
                      setRut(formateado);
                      if (limpiarRut(e.target.value).length >= 8) {
                        if (!validarRut(e.target.value)) {
                          setRutError('RUT inválido. Verifica el dígito verificador.');
                        } else {
                          setRutError('');
                        }
                      } else {
                        setRutError('');
                      }
                    }}
                    maxLength={12}
                    required={tipoDocumento === 'cedula'}
                    style={rutError ? { borderColor: '#dc3545' } : {}}
                  />
                  {rutError && <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>{rutError}</small>}
                </div>
              )}

              {tipoDocumento === 'pasaporte' && (
                <div className="input-group">
                  <label>Número de Pasaporte<RequiredAsterisk /></label>
                  <input
                    type="text"
                    placeholder="Ingrese su n° de pasaporte"
                    value={numeroPasaporte}
                    onChange={(e) => setNumeroPasaporte(e.target.value)}
                    required={tipoDocumento === 'pasaporte'}
                  />
                </div>
              )}

              <div className="input-group">
                <label>Dirección</label>
                <input type="text" placeholder="Ej: Av. Siempre Viva 123" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
              </div>

              <div className="input-group">
                <label>Contraseña<RequiredAsterisk /></label>
                <input type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="input-group">
                <label>Confirmar Contraseña<RequiredAsterisk /></label>
                <input type="password" placeholder="Repite tu contraseña" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>

              {error && <div className="alert alert-danger" style={{ padding: '10px', margin: '10px 0', borderRadius: '5px', backgroundColor: '#f8d7da', color: '#721c24' }}>{error}</div>}

              <div className="form-actions-row">
                <button type="submit" className="btn btn-primary" disabled={cargando}>
                  {cargando ? 'Creando cuenta...' : 'Registrarse'}
                </button>

                <Link
                  to={isModal ? "/login?modal=true" : "/login"}
                  className="create-account-link"
                >
                  ¿Ya tienes una cuenta?
                </Link>
              </div>
            </form>

            {!isModal && (
              <div className="social-login">
                <h4>O registrarse con</h4>
                <div className="social-icons">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('google'); }} title="Registrarse con Google"><FaGoogle /></a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('facebook'); }} title="Registrarse con Facebook"><FaFacebookF /></a>
                </div>
              </div>
            )}
          </div>

          {!isModal && (
            <div className="copyright">
              <p>&copy; {new Date().getFullYear()} XCoin. Todos los derechos reservados.</p>
            </div>
          )}

        </div>
      </div>
      <div className="background-side" style={backgroundSideStyle}></div>
    </div>
  );
};

export default RegisterPage;
